import React, { useState } from "react";
import { storage } from "../firebase"; // Make sure this is correctly configured
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Button } from "../components/ui/button";
import axios from "axios";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from 'react-toastify';

interface AddBookFormProps {
    onClose?: () => void;
}

interface Book {
    title: string;
    price: string;
    purchaseLink: string;
    blurb: string;
    imageUrl: string;
    userId: string;
}

const AddBookForm: React.FC<AddBookFormProps> = ({ onClose }) => {
    const { user } = useAuth();
    const [book, setBook] = useState<Omit<Book, "imageUrl" | "userId">>({
        title: "",
        price: "",
        purchaseLink: "",
        blurb: "",
    });
    const [image, setImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const userId = user?.uid ?? "";

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setBook(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            let imageUrl = "";
            if (image) {
                const imageRef = ref(storage, `books/${Date.now()}_${image.name}`);
                await uploadBytes(imageRef, image);
                imageUrl = await getDownloadURL(imageRef);
            }

            const fullBook: Book = {
                ...book,
                imageUrl,
                userId,
            };

            console.log("Book to save:", fullBook);
            await axios.post(api.saveBook(), fullBook);

            // Reset form
            setBook({ title: "", price: "", purchaseLink: "", blurb: "" });
            setImage(null);
            setImagePreview(null);

            toast.success("Book saved!", {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });

        } catch (err) {
            console.error("Error uploading book:", err);
            toast.error("Something went wrong.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="max-w-md mx-auto bg-white p-6 rounded shadow space-y-4 mb-4"
        >
            <h2 className="text-2xl font-bold mb-4">Add a Book</h2>

            {imagePreview && (
                <img src={imagePreview} alt="Preview" className="h-48 object-contain mb-4" />
            )}
            <input type="file" accept="image/*" onChange={handleImageChange} />

            <input
                type="text"
                name="title"
                placeholder="Title"
                value={book.title}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
                required
            />

            <input
                type="text"
                name="price"
                placeholder="Price (e.g. $14.99)"
                value={book.price}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
            />

            <input
                type="url"
                name="purchaseLink"
                placeholder="Purchase Link (https://...)"
                value={book.purchaseLink}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
            />

            <textarea
                name="blurb"
                placeholder="Blurb"
                value={book.blurb}
                onChange={handleInputChange}
                className="w-full p-2 border rounded h-32"
            />

            <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full"
            >
                {isSubmitting ? "Saving..." : "Add Book"}
            </Button>

        </form>
    );
};

export default AddBookForm;
